import { css } from '@emotion/react';
import { Formik } from 'formik';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useMarketsByUserId, useUserMutators } from '@kaiizen/kai-pro-api-react-state';
import { ErrorWithStatus } from '@kaiizen/zod-less-api';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { ANALYTICS_EVENTS, trackEvent } from '../analytics';
import searchIcon from '../Assets/search-v2.svg';
import { PageLayout } from '../Layout';
import { normalizeEmail, usePageTitle, usePersistedSearch, usePersistedSort } from '../Lib';
import { NoRecordsPlaceholder } from '../Partials';
import { useAuth, useKaiProApiClient, useOrgMarkets, useToast } from '../State';
import {
  Button,
  DataTable,
  FlyOut,
  FormFieldsRequirementNote,
  FormikLabeledInput,
  FormikLabeledMultiSelect,
  FormikLabeledSelect,
  StyledForm,
} from '../Widgets';
import { GenericLoadingPage } from './GenericPages';

const SEARCH_BAR_STYLE = css`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: 1px solid #cfcfcf;
  height: 42px;
  width: 360px;
  padding: 0 10px;
`;

const SEARCH_ICON_STYLE = css`
  width: 18px;
  height: 18px;
  opacity: 0.45;
  filter: brightness(0) saturate(100%);
`;

const SEARCH_COLLAPSED_BUTTON_STYLE = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  background: #ffffff;
  border: 1px solid #cfcfcf;
  cursor: pointer;
  padding: 0;
`;

const SEARCH_ICON_BUTTON_STYLE = css`
  label: SEARCH_ICON_BUTTON_STYLE;
  background: transparent;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const SEARCH_INPUT_STYLE = css`
  label: SEARCH_INPUT_STYLE;
  flex: 1;
  background: transparent;
  border: none;
  color: #1f1f1f;
  outline: none;

  &::placeholder {
    color: #767676;
  }

  &::-webkit-search-cancel-button {
    -webkit-appearance: none;
    appearance: none;
    display: none;
  }
`;

const SEARCH_CLEAR_BUTTON_STYLE = css`
  label: SEARCH_CLEAR_BUTTON_STYLE;
  position: relative;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 18px;
  height: 18px;
  flex-shrink: 0;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 8px;
    top: 1px;
    width: 2px;
    height: 16px;
    background: #1f1f1f;
    border-radius: 1px;
  }

  &::before {
    transform: rotate(45deg);
  }

  &::after {
    transform: rotate(-45deg);
  }
`;

const HEADER_ACTIONS_STYLE = css`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserFlyoutForm = (
  { flyoutOpen, closeFlyout, userToEdit, updateUser, addUser, updateMarketsForUser, isAddingUser, addUserError, readOnly, currentUserId }: {
    flyoutOpen: boolean;
    closeFlyout: () => void;
    userToEdit?: any;
    updateUser: (userId: string, role: string, phoneNumber?: string, name?: string, email?: string) => Promise<void>;
    addUser: (phoneNumber: string, name: string, role: string, email?: string) => Promise<string>;
    updateMarketsForUser: (userId: string, marketIds: string[]) => Promise<void>;
    isAddingUser: boolean;
    addUserError: string;
    readOnly?: boolean;
    currentUserId?: string;
  },
) => {
  const { markets } = useOrgMarkets();
  const { showToast, showErrorToast } = useToast();
  const { markets: marketsAssignedToUser } = useMarketsByUserId(userToEdit?.id);

  return (
    <FlyOut
      open={flyoutOpen}
      flyOutClosed={closeFlyout}
      title={userToEdit
        ? (readOnly ? 'User Details' : 'Edit User Details')
        : 'Add User to Organization'}
      subheader={userToEdit || readOnly ? undefined : <FormFieldsRequirementNote variant='optional' />}
    >
      <Formik<{ phoneNumber: string; name: string; email?: string; role: string; marketIds: string[] }>
        initialValues={{
          phoneNumber: userToEdit?.phoneNumber || '',
          name: userToEdit?.name || '',
          email: userToEdit?.email || '',
          role: userToEdit?.role || 'fieldUser',
          marketIds: marketsAssignedToUser?.map(market => market.id) ?? [],
        }}
        enableReinitialize
        onSubmit={async values => {
          try {
            if (readOnly) return;
            if (userToEdit) {
              await updateUser(userToEdit.id, values.role, values.phoneNumber, values.name, values.email);
              await updateMarketsForUser(userToEdit.id, values.marketIds);
              showToast('User updated successfully', 'success');
            } else {
              if (!values.name) {
                showErrorToast('Name is required');
                return;
              }
              const addedUserId = await addUser(values.phoneNumber, values.name, values.role, values.email);
              if (values.marketIds && values.marketIds.length > 0) {
                await updateMarketsForUser(addedUserId, values.marketIds);
              }
              showToast('User added successfully', 'success');
            }
            closeFlyout();
          } catch (error) {
            showErrorToast(error as ErrorWithStatus, userToEdit ? 'update user' : 'add user');
          }
        }}
      >
        {({ handleSubmit, values }) => (
          <StyledForm onSubmit={handleSubmit}>
            <span>
              {userToEdit
                ? userToEdit.id === currentUserId
                  ? 'Update your details and market assignments in your organization. You cannot change your own role.'
                  : "Update the user's details and market assignments in your organization."
                : "Enter the user's information to add them to your organization."}
            </span>
            <FormikLabeledInput
              name='phoneNumber'
              label='Phone Number'
              placeholder='+1234567890'
              optional
              disabled={readOnly}
            />
            <FormikLabeledInput
              name='name'
              label='Name'
              placeholder="User's full name"
              disabled={readOnly}
            />
            <FormikLabeledInput
              name='email'
              label='Email'
              placeholder='user@email.com'
              optional
              disabled={readOnly}
            />
            <FormikLabeledSelect
              name='role'
              label='Role'
              items={[
                { value: 'fieldUser', label: 'Field User' },
                { value: 'manager', label: 'Manager' },
                { value: 'admin', label: 'Admin' },
                { value: 'viewer', label: 'Viewer' },
              ]}
              disabled={readOnly || userToEdit?.id === currentUserId}
            />
            {markets && (
              <FormikLabeledMultiSelect
                name='marketIds'
                label='Markets'
                placeholder='Add markets...'
                items={markets.map(m => ({ value: m.id, label: m.name }))}
                disabled={readOnly}
              />
            )}
            {addUserError && (
              <div style={{ color: 'red', marginBottom: '10px', fontSize: pxToRem(14) }}>
                {addUserError}
              </div>
            )}
            {!readOnly && (
              <Button
                color='green'
                disabled={(!userToEdit && (!values.phoneNumber && !values.email)) || (!userToEdit && !values.name) || isAddingUser}
                type='submit'
              >
                {isAddingUser ? 'Adding...' : userToEdit ? 'Update User' : 'Add User'}
              </Button>
            )}
          </StyledForm>
        )}
      </Formik>
    </FlyOut>
  );
};

export const UsersPage = () => {
  let { id: userId } = useParams();
  usePageTitle('Users');
  const navigate = useNavigate();
  const { orgId, token, user, userCan } = useAuth();
  const kaiProApiClient = useKaiProApiClient();
  const { markets } = useOrgMarkets();
  const { refetchAllUsers } = useUserMutators();
  const [users, setUsers] = useState<any[] | null>(null);
  const [userMarkets, setUserMarkets] = useState<Record<string, string[]>>({});
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [addUserError, setAddUserError] = useState('');
  const { searchText, setSearchText, debouncedSearchText, clearSearch } = usePersistedSearch('users:search');
  const { order: sortOrder, orderBy: sortOrderBy, onSortChange } = usePersistedSort('users:sort', 'asc', 'name');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const canCreateUser = userCan('create', 'UserToOrg');
  const canUpdateUser = userCan('update', 'UserToOrg');
  const canDeleteUser = userCan('delete', 'UserToOrg');

  const loadUsersWithMarkets = useCallback(async () => {
    const fetchedUsers = await kaiProApiClient.getUsers({ search: debouncedSearchText || undefined });
    setUsers(fetchedUsers);
    if (!markets || markets.length === 0) {
      setUserMarkets({});
      return;
    }
    const marketsUsers = await Promise.all(
      markets.map((market: any) =>
        kaiProApiClient.getUsersByOrgMarketId(market.id).then(marketUsers => ({
          marketName: market.name,
          users: marketUsers,
        }))
      ),
    );
    const aggregated: Record<string, string[]> = {};
    marketsUsers.forEach(({ marketName, users }) => {
      users.forEach(userInMarket => {
        aggregated[userInMarket.id] = [...(aggregated[userInMarket.id] || []), marketName];
      });
    });
    setUserMarkets(aggregated);
  }, [kaiProApiClient, markets, debouncedSearchText]);

  useEffect(() => {
    if (isSearchExpanded) searchInputRef.current?.focus();
  }, [isSearchExpanded]);

  useEffect(() => {
    if (!orgId || !token) {
      setUsers(null);
      setUserMarkets({});
      return;
    }
    loadUsersWithMarkets();
  }, [orgId, token, markets, loadUsersWithMarkets]);

  if (!users) return <GenericLoadingPage />;

  const userToEdit = users?.find(u => u.id === userId);
  const newUser = userId === 'new';
  if (newUser) userId = undefined;
  const flyoutOpen = newUser || !!userToEdit;

  const addUser = async (phoneNumber: string, name: string, role: string, email?: string): Promise<string> => {
    const trimmedPhone = phoneNumber.trim();
    const normalizedEmail = email ? normalizeEmail(email) : '';
    if (!token || !orgId || (!trimmedPhone && !normalizedEmail)) {
      setAddUserError('Phone number or email is required');
      throw new Error('Phone number or email is required');
    }

    setIsAddingUser(true);
    setAddUserError('');

    try {
      const addedUser = await kaiProApiClient.addUserToOrgByPhoneNumber(
        orgId,
        trimmedPhone || undefined,
        role as any,
        name.trim() || undefined,
        normalizedEmail || undefined,
      );
      await loadUsersWithMarkets();
      trackEvent(ANALYTICS_EVENTS.userAdded, {
        userId: addedUser.userId,
        phoneNumber: trimmedPhone,
        name: name.trim() || undefined,
        role,
        orgId,
        email: normalizedEmail || undefined,
      });
      return addedUser.userId;
    } catch (error: any) {
      setAddUserError(error.message || 'Failed to add user');
      throw error;
    } finally {
      setIsAddingUser(false);
    }
  };

  const updateUser = async (userId: string, role: string, phoneNumber?: string, name?: string, email?: string) => {
    const existingUser = users?.find(u => u.id === userId);
    if (!existingUser) return;
    const normalizedPhone = (phoneNumber ?? existingUser.phoneNumber)?.trim() || undefined;
    const normalizedName = (name ?? existingUser.name)?.trim() || undefined;
    const emailCandidate = email ?? existingUser.email;
    const normalizedEmail = emailCandidate?.trim() ? normalizeEmail(emailCandidate) : undefined;
    await kaiProApiClient.updateUser(userId, { phoneNumber: normalizedPhone, name: normalizedName, email: normalizedEmail });
    if (existingUser.role !== role) await kaiProApiClient.updateUserOrgRole(orgId!, userId, role as any);
    await loadUsersWithMarkets();
    trackEvent(ANALYTICS_EVENTS.userUpdated, {
      userId,
      role,
      orgId: orgId!,
      phoneNumber: normalizedPhone,
      name: normalizedName,
      email: normalizedEmail,
    });
  };

  const updateMarketsForUser = async (userId: string, marketIds: string[]) => {
    if (!markets) return;

    const existingMarketIds: string[] = [];
    for (const market of markets) {
      const users = await kaiProApiClient.getUsersByOrgMarketId(market.id);
      if (users.some(u => u.id === userId)) {
        existingMarketIds.push(market.id);
      }
    }

    const marketsToAdd = marketIds.filter(id => !existingMarketIds.includes(id));
    const marketsToRemove = existingMarketIds.filter(id => !marketIds.includes(id));

    await Promise.all([
      ...marketsToAdd.map(marketId => kaiProApiClient.addUserToOrgMarket(marketId, userId)),
      ...marketsToRemove.map(marketId => kaiProApiClient.removeUserFromOrgMarket(marketId, userId)),
    ]);

    await Promise.all([
      refetchAllUsers(),
      loadUsersWithMarkets(),
    ]);
  };

  const removeUser = async (userId: string) => {
    try {
      await kaiProApiClient.removeUserFromOrg(orgId!, userId);
      await loadUsersWithMarkets();
      trackEvent(ANALYTICS_EVENTS.userRemoved, {
        userId,
        orgId: orgId!,
      });
    } catch (error: any) {
      setAddUserError(error.message || 'Failed to remove user');
    }
  };

  const columns = [
    { name: 'name', displayName: 'Name', sortable: true },
    { name: 'phoneNumber', displayName: 'Phone', sortable: true },
    { name: 'email', displayName: 'Email', sortable: true },
    { name: 'role', displayName: 'Role', sortable: true },
    { name: 'markets', displayName: 'Markets', sortable: false },
    { name: 'createdAt', displayName: 'Created', sortable: true, sortMethod: 'date' as const, format: (value: string) => new Date(value).toLocaleString() },
    { name: 'updatedAt', displayName: 'Updated', sortable: true, sortMethod: 'date' as const, format: (value: string) => new Date(value).toLocaleString() },
  ];

  const loading = users === null;
  const haveUsers = !loading && users.length > 0;
  const noUsers = !loading && users.length === 0;
  const hasActiveSearch = !!searchText.trim();
  const shouldShowExpandedSearch = isSearchExpanded || !!searchText.trim();

  const displayUsers = users === null ? null : users.map(userItem => ({
    id: userItem.id,
    name: userItem.name || 'No name',
    phoneNumber: userItem.phoneNumber,
    email: userItem.email,
    role: userItem.role,
    markets: userMarkets[userItem.id]?.join(', ') || '',
    createdAt: userItem.createdAt,
    updatedAt: userItem.updatedAt,
    link: `/user/${userItem.id}`,
    menu: [
      {
        label: canUpdateUser ? 'Edit User' : 'View User',
        action: () => {
          navigate(`/user/${userItem.id}`);
        },
      },
      ...(canDeleteUser
        ? [{
          label: 'Remove User',
          action: () => {
            // Prevent users from removing themselves
            if (userItem.id === user?.id) {
              alert('You cannot remove yourself from the organization');
              return;
            }

            if (confirm(`Are you sure you want to remove ${userItem.name || userItem.phoneNumber} from the organization?`)) {
              removeUser(userItem.id);
            }
          },
        }]
        : []),
    ],
  }));

  return (
    <PageLayout
      title='Users'
      contentLayoutStyling={'standard'}
      subHeaderButtons={
        <div css={HEADER_ACTIONS_STYLE}>
          {shouldShowExpandedSearch
            ? (
              <div css={SEARCH_BAR_STYLE}>
                <button
                  css={SEARCH_ICON_BUTTON_STYLE}
                  type='button'
                  onClick={() => {
                    if (!searchText.trim()) setIsSearchExpanded(false);
                  }}
                  aria-label='Search'
                >
                  <img src={searchIcon} css={SEARCH_ICON_STYLE} alt='Search' />
                </button>
                <input
                  ref={searchInputRef}
                  css={SEARCH_INPUT_STYLE}
                  type='search'
                  value={searchText}
                  onChange={event => setSearchText(event.target.value)}
                  placeholder='Search by name, email, or phone'
                  aria-label='Search users'
                />
                {!!searchText.trim() && (
                  <button
                    css={SEARCH_CLEAR_BUTTON_STYLE}
                    type='button'
                    onClick={() => {
                      clearSearch();
                      setIsSearchExpanded(false);
                    }}
                    aria-label='Clear and close user search'
                  />
                )}
              </div>
            )
            : (
              <button
                key='userSearchCollapsed'
                css={SEARCH_COLLAPSED_BUTTON_STYLE}
                type='button'
                onClick={() => setIsSearchExpanded(true)}
                aria-label='Open user search'
              >
                <img src={searchIcon} css={SEARCH_ICON_STYLE} alt='Search' />
              </button>
            )}
          {canCreateUser
            && (
              <Button
                color='bluePrimary'
                onClick={() => navigate('/user/new')}
              >
                Add User
              </Button>
            )}
        </div>
      }
    >
      {noUsers
        ? (
          <NoRecordsPlaceholder
            recordType='Users'
            description={hasActiveSearch
              ? 'No users found. Try a different name, email, or phone.'
              : canCreateUser
              ? 'Start by adding a user to your organization.'
              : 'No users have been added yet.'}
            createButtonText={hasActiveSearch ? 'Clear Search' : (canCreateUser ? 'Add User' : undefined)}
            createFirstRecord={hasActiveSearch
              ? (() => {
                clearSearch();
                setIsSearchExpanded(false);
              })
              : (canCreateUser ? () => navigate('/user/new') : undefined)}
            iconType='users'
          />
        )
        : haveUsers && displayUsers
        ? (
          <DataTable
            recordType='user'
            items={displayUsers}
            columns={columns}
            defaultOrder={sortOrder}
            defaultOrderBy={sortOrderBy}
            onSortChange={onSortChange}
          />
        )
        : null}

      <UserFlyoutForm
        flyoutOpen={flyoutOpen}
        closeFlyout={() => {
          setAddUserError('');
          navigate('/users');
        }}
        userToEdit={userToEdit}
        updateUser={updateUser}
        addUser={addUser}
        updateMarketsForUser={updateMarketsForUser}
        isAddingUser={isAddingUser}
        addUserError={addUserError}
        readOnly={!canUpdateUser && !!userToEdit}
        currentUserId={user?.id}
      />
    </PageLayout>
  );
};
